import * as React from 'react';
import {
  CreditCard,
  Home,
  Map,
  Sparkles,
  Moon,
  Sun,
  Settings,
  Calendar,
  CalendarRange,
  CalendarClock,
  User,
} from 'lucide-react';

import { NavMain } from '@/components/nav-main';
import { NavProjects } from '@/components/nav-projects';
import { NavSecondary } from '@/components/nav-secondary';
import { NavUser } from '@/components/nav-user';
import { TeamSwitcher } from '@/components/team-switcher';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '@/components/ui/sidebar';
import { useEffect, useState } from 'react';
import type {
  UserResponse,
  ListOrganisationsResponse,
  OrganisationInviteRead,
} from '@/client/types.gen';
import {
  getCurrentUser,
  listOrganisations,
  getOrganisationUserOnboardingDetails,
  listInvitesForUser,
} from '@/client/sdk.gen';
import { WhatsNewModal } from '@/components/whats-new-modal';
import { CalendarSetupOnboarding } from '@/components/calendar-setup-onboarding';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/components/theme-provider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { identifyUser } from '@/utils/featureFlags';
import { SELECTED_ORG_KEY } from '@/constants';
import { PendingInvitesAlert } from '@/components/pending-invites-alert';

const data = {
  navMain: {
    title: 'Platform',
    items: [
      {
        title: 'Home',
        url: '/dashboard',
        icon: Home,
      },
      {
        title: 'Availability',
        url: '/dashboard/availability',
        icon: CalendarClock,
      },
      {
        title: 'Appointment Types',
        url: '/dashboard/event-types',
        icon: CalendarRange,
      },
      {
        title: 'Appointments',
        url: '/dashboard/appointments',
        icon: Calendar,
      },
      {
        title: 'Patients',
        url: '/dashboard/patients',
        icon: User,
      },
    ],
  },
  orgSettings: {
    title: 'Team Management',
    items: [
      // {
      //   title: 'Billing',
      //   url: '/dashboard/team-management/billing',
      //   icon: CreditCard,
      // },
      {
        title: 'Settings',
        url: '/dashboard/team-management/settings',
        icon: Settings,
      },
    ],
  },
  projects: [],
  navSecondary: [
    // {
    //   title: 'Product Roadmap',
    //   url: 'https://roadmap.gcal.ai',
    //   icon: Map,
    //   external: true,
    // },
  ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { setTheme } = useTheme();
  const [user, setUser] = useState<UserResponse | null>(null);
  const [organisations, setOrganisations] = useState<
    ListOrganisationsResponse['organisations']
  >([]);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [showCalendarSetup, setShowCalendarSetup] = useState(false);
  const [calendarSetForEvents, setCalendarSetForEvents] = useState(true);
  const [initialStep, setInitialStep] = useState(0);
  const [pendingInvites, setPendingInvites] = useState<
    OrganisationInviteRead[]
  >([]);
  const [isAdmin, setIsAdmin] = useState(false);
  console.log(organisations);
  console.log(calendarSetForEvents);
  const fetchOrganisationData = async () => {
    try {
      const response = await listOrganisations();
      if (response.data) {
        setOrganisations(response.data.organisations);
      }

      // Get onboarding details from the dedicated API
      const onboardingResponse = await getOrganisationUserOnboardingDetails();
      if (onboardingResponse.data) {
        setCalendarSetForEvents(
          onboardingResponse.data.calendar_set_for_events
        );

        // Determine if setup needed and which step to start from
        if (
          !onboardingResponse.data.calendar_set_for_events ||
          !onboardingResponse.data.default_schedule_exists
        ) {
          setShowCalendarSetup(true);

          // If calendar is set but schedule isn't, start from schedule step
          if (onboardingResponse.data.calendar_set_for_events) {
            setInitialStep(1);
          } else {
            setInitialStep(0);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch organisation data:', error);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const response = await listInvitesForUser();
      if (response.data) {
        // Filter for pending invites (those that have not been accepted)
        const pending = response.data.invites.filter(
          (invite) => !invite.accepted_at
        );
        setPendingInvites(pending);
      }
    } catch (error) {
      console.error('Failed to fetch pending invites:', error);
    }
  };

  // Add event listener for invite acceptance
  useEffect(() => {
    // Handler for the custom event
    const handleInviteAccepted = () => {
      fetchPendingInvites();
    };

    // Add event listener
    window.addEventListener('refresh-pending-invites', handleInviteAccepted);

    // Clean up
    return () => {
      window.removeEventListener(
        'refresh-pending-invites',
        handleInviteAccepted
      );
    };
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const userResponse = await getCurrentUser();

        if (userResponse.data) {
          setUser(userResponse.data);
          // Check if user has role ADMIN in current organisation
          setIsAdmin(
            userResponse.data.role_in_current_organisation === 'ADMIN'
          );

          // Check if user has seen the What's New modal
          const hasSeenWhatsNew = localStorage.getItem('hasSeenWhatsNew');
          if (!hasSeenWhatsNew) {
            setShowWhatsNew(true);
            localStorage.setItem('hasSeenWhatsNew', 'true');
          }
          const selectedOrgId = localStorage.getItem(SELECTED_ORG_KEY);
          identifyUser(
            userResponse.data.user_id,
            userResponse.data.email,
            selectedOrgId ?? '',
            userResponse.data.name
          );

          // Fetch organisation data after user data is loaded
          await fetchOrganisationData();

          // Fetch pending invites
          await fetchPendingInvites();
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    }

    fetchData();
  }, []);

  const handleCalendarSetupComplete = async () => {
    setCalendarSetForEvents(true);
    setShowCalendarSetup(false);
    // Refresh organisation data to verify the update
    await fetchOrganisationData();
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher />
      </SidebarHeader>
      <SidebarContent>
        {pendingInvites.length > 0 && (
          <div className="mb-4 px-4">
            <PendingInvitesAlert invites={pendingInvites} />
          </div>
        )}
        <NavMain title={data.navMain.title} items={data.navMain.items} />
        {isAdmin && (
          <NavMain
            title={data.orgSettings.title}
            items={data.orgSettings.items}
          />
        )}
        {data.projects.length > 0 && <NavProjects projects={data.projects} />}
        <NavSecondary items={data.navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 group-data-[collapsible=icon]:hidden">
            {/* <Button
              variant="outline"
              className="flex-1 justify-start gap-2"
              onClick={() => setShowWhatsNew(true)}
            >
              <Sparkles className="h-4 w-4 text-purple-500" />
              What's New
            </Button> */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                  <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">Toggle theme</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  Dark
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  System
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {user && (
            <NavUser
              user={{
                name: user.name,
                email: user.email,
                profilePictureUrl: user.profile_picture_url,
              }}
            />
          )}
        </div>
      </SidebarFooter>
      <SidebarRail />
      <WhatsNewModal isOpen={showWhatsNew} onOpenChange={setShowWhatsNew} />
      {user && (
        <CalendarSetupOnboarding
          isOpen={showCalendarSetup}
          onComplete={handleCalendarSetupComplete}
          userEmail={user.email}
          organisationId={user.organisation_id}
          initialStep={initialStep}
        />
      )}
    </Sidebar>
  );
}
